import { useEffect, useRef, type ReactNode } from 'react';
import { ConfigProvider, Flex, Slider } from 'antd';
import {
  ArrowClockwiseIcon,
  ArrowCounterClockwiseIcon,
  ArticleIcon,
  PauseIcon,
  PlayIcon,
  WaveformIcon,
} from '@phosphor-icons/react';
import { useAudioPlayback } from './hooks/useAudioPlayback';
import { AudioPlaybackInfo, TrackKey, TrackPlayerProps } from './types';
import {
  StyledCover,
  StyledCoverFrame,
  StyledPlayButton,
  StyledPlayerCard,
  StyledRateButton,
  StyledSkipButton,
  StyledSubtitle,
  StyledTimeLabel,
  StyledTitle,
  StyledWave,
} from './TrackPlayer.styles';
import { Label } from '../../../Label';
import { colors, spacing } from '../../../../theme-v2';
import { formatTime, PLAYBACK_RATES, SKIP_SECONDS } from './utils';

/**
 * Static cover icon shown before/while paused — the only per-variant visual
 * difference besides the card's gradient (see TrackPlayer.styles' GRADIENT).
 * While playing, both variants show the same animated wave instead.
 */
const PAUSED_ICON: Record<TrackKey, ReactNode> = {
  podcast: <WaveformIcon size={56} weight="duotone" />,
  audioDoc: <ArticleIcon size={56} weight="duotone" />,
};

export const TrackPlayer = ({
  trackKey,
  track,
  t,
  analyticEvents,
  className,
  autoPlay,
  speedControl = true,
  initialTime,
  initialSpeed,
  progressEvents,
}: TrackPlayerProps) => {
  const {
    playing,
    currentTime,
    duration,
    toggle,
    seekTo,
    skip,
    playbackRate,
    setPlaybackRate,
  } = useAudioPlayback(
    track.url,
    () =>
      // Track end — the playhead is at the very end when `ended` fires.
      analyticEvents?.onComplete?.(playbackInfo(duration)),
    { autoPlay, initialTime, initialSpeed, progressEvents },
  );

  /** Snapshot for analytics callbacks, at the given playhead position. */
  const playbackInfo = (positionSeconds: number): AudioPlaybackInfo => ({
    track: trackKey,
    positionSeconds,
    durationSeconds: duration,
  });

  // One `onSeek` per progress-bar gesture: the slider's onChange fires
  // continuously while scrubbing, so remember where the playhead was when the
  // gesture started and emit a single from → to on onChangeComplete.
  const seekOriginRef = useRef<number | null>(null);

  // Close a playing session when it ends without a pause click: unmount
  // (track switch, hub collapse, navigation — useAudioPlayback's cleanup
  // pauses the element, so the sound really stops) and pagehide (tab close
  // mid-listen, best-effort like every unload-time event). Latest values live
  // in a ref because both callers fire outside the render that armed them;
  // `playing` is cleared after emitting so the pagehide → unmount sequence
  // can't double-report.
  const sessionRef = useRef({
    playing,
    currentTime,
    duration,
    trackKey,
    analyticEvents,
  });
  useEffect(() => {
    sessionRef.current = {
      playing,
      currentTime,
      duration,
      trackKey,
      analyticEvents,
    };
  }, [playing, currentTime, duration, trackKey, analyticEvents]);

  useEffect(() => {
    const endSession = () => {
      const s = sessionRef.current;
      if (!s.playing) return;
      s.playing = false;
      s.analyticEvents?.onPause?.({
        track: s.trackKey,
        positionSeconds: s.currentTime,
        durationSeconds: s.duration,
      });
    };
    window.addEventListener('pagehide', endSession);
    return () => {
      window.removeEventListener('pagehide', endSession);
      endSession();
    };
  }, []);

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const label =
    trackKey === 'audioDoc'
      ? t('experienceV2.audio.audioDocTab')
      : t('experienceV2.audio.podcastTab');

  // TODO(MED-6934): episode/chapter label ("Ep. 3" / "Ch. 1") — the generated
  // asset doesn't carry a section index yet; re-enable once the backend
  // provides `track.sectionIndex`:
  // const episodeLabel = trackKey === 'podcast' ? 'Ep.' : 'Ch.';
  // <Label>{`${label} · ${episodeLabel} ${track.sectionIndex}`.toUpperCase()}</Label>

  // Duration in whole minutes. The loaded audio element wins over the asset
  // metadata so this label can never disagree with the seek bar's timer.
  const durationSeconds = duration > 0 ? duration : track.durationSeconds;
  const minutes = durationSeconds
    ? Math.max(1, Math.round(durationSeconds / 60))
    : undefined;

  // Real cover art wins when the asset has one; otherwise the frame shows the
  // shared "playing" wave, or the variant's static icon.
  let coverContent: ReactNode;
  if (track.coverImageUrl) {
    coverContent = <StyledCover src={track.coverImageUrl} alt="" />;
  } else if (playing) {
    coverContent = (
      <StyledWave>
        <span />
        <span />
        <span />
        <span />
      </StyledWave>
    );
  } else {
    coverContent = PAUSED_ICON[trackKey];
  }

  return (
    <StyledPlayerCard $trackKey={trackKey} className={className}>
      <StyledCoverFrame>{coverContent}</StyledCoverFrame>

      <Flex vertical align="center" gap={spacing.xs}>
        <Label style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
          {label.toUpperCase()}
        </Label>
        <StyledTitle>{track.title || label}</StyledTitle>
        {minutes !== undefined && (
          <StyledSubtitle $muted>
            {t('experienceV2.audio.duration', { minutes })}
          </StyledSubtitle>
        )}
      </Flex>

      <Flex vertical gap={spacing.xs} style={{ width: '100%' }}>
        <ConfigProvider
          theme={{
            components: {
              Slider: {
                handleSize: 0,
                railSize: 4,
                handleSizeHover: 0,
                handleLineWidth: 0,
                handleLineWidthHover: 0,
                handleActiveOutlineColor: 'transparent',
                trackBg: colors.text.onAccent,
                trackHoverBg: colors.text.onAccent,
                railBg: 'rgba(255, 255, 255, 0.3)',
              },
            },
          }}
        >
          <Slider
            value={progress}
            onChange={(value) => {
              // First movement of a gesture: remember the pre-seek playhead.
              seekOriginRef.current ??= currentTime;
              seekTo((value / 100) * duration);
            }}
            onChangeComplete={(value) => {
              const from = seekOriginRef.current ?? currentTime;
              seekOriginRef.current = null;
              analyticEvents?.onSeek?.(
                from,
                (value / 100) * duration,
                playbackInfo(from),
              );
            }}
            tooltip={{ formatter: null }}
            min={0}
            max={100}
            step={0.1}
            style={{ margin: 0 }}
          />
        </ConfigProvider>
        <Flex justify="space-between">
          <StyledTimeLabel>{formatTime(currentTime)}</StyledTimeLabel>
          <StyledTimeLabel>{formatTime(duration)}</StyledTimeLabel>
        </Flex>
      </Flex>

      <Flex align="center" justify="center" gap={spacing.md}>
        <StyledSkipButton
          type="button"
          onClick={() => {
            analyticEvents?.onSkip?.('back', playbackInfo(currentTime));
            skip(-SKIP_SECONDS);
          }}
          aria-label={t('experienceV2.audio.rewind', {
            seconds: SKIP_SECONDS,
          })}
        >
          <ArrowCounterClockwiseIcon size={18} weight="bold" />
          <span>{SKIP_SECONDS}</span>
        </StyledSkipButton>

        <StyledPlayButton
          type="button"
          onClick={() => {
            const info = playbackInfo(currentTime);
            if (playing) analyticEvents?.onPause?.(info);
            else analyticEvents?.onPlay?.(info);
            toggle();
          }}
          aria-label={
            playing
              ? t('experienceV2.audio.pause')
              : t('experienceV2.audio.play')
          }
        >
          {playing ? (
            <PauseIcon size={22} weight="fill" />
          ) : (
            <PlayIcon size={22} weight="fill" />
          )}
        </StyledPlayButton>

        <StyledSkipButton
          type="button"
          onClick={() => {
            analyticEvents?.onSkip?.('forward', playbackInfo(currentTime));
            skip(SKIP_SECONDS);
          }}
          aria-label={t('experienceV2.audio.forward', {
            seconds: SKIP_SECONDS,
          })}
        >
          <ArrowClockwiseIcon size={18} weight="bold" />
          <span>{SKIP_SECONDS}</span>
        </StyledSkipButton>
      </Flex>

      {speedControl && (
        <Flex align="center" justify="center" gap={spacing.xs} wrap="wrap">
          {PLAYBACK_RATES.map((rate) => (
            <StyledRateButton
              key={rate}
              type="button"
              $active={playbackRate === rate}
              onClick={() => {
                if (rate === playbackRate) return; // re-clicking the active rate
                analyticEvents?.onSpeedChange?.(
                  rate,
                  playbackInfo(currentTime),
                );
                setPlaybackRate(rate);
              }}
            >
              {rate}x
            </StyledRateButton>
          ))}
        </Flex>
      )}
    </StyledPlayerCard>
  );
};
